import {
  useRequest,
  useApproveRequest,
  useRejectRequest,
} from "@/hooks/useAdminDashboardService";
import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import { formatDateUS } from "@/utils/dateUtils";
import { toast } from "react-toastify";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

interface ContactRequestItem {
  companyEmail: string;
  id: string;
  companyName: string;
  description: string;
  createdAt: string;
  status?: "PENDING" | "APPROVED" | "REJECTED";
  requestContext?: "UPGRADE" | "REGISTRATION";
}

const ContactRequestsPage = () => {
  const { isDark } = useDashboardTheme();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const pageSize = 10;

  interface ContactRequestResponseData {
    items: ContactRequestItem[];
    totalCount: number;
    page?: number;
    pageSize?: number;
    totalPages?: number;
  }

  interface UseRequestResult {
    data?: {
      data?: ContactRequestResponseData;
    };
    isLoading: boolean;
    isError: boolean;
  }
  const { data, isLoading, isError } = useRequest({
    page,
    pageSize,
    search: searchQuery || undefined,
    orderBy: "createdAt",
    order: "desc",
    requestContext: "UPGRADE",
  }) as UseRequestResult;
  const requests = data?.data?.items ?? [];
  const totalCount = data?.data?.totalCount || 0;
  const totalPagesFromApi = data?.data?.totalPages;

  const approveMutation = useApproveRequest();
  const rejectMutation = useRejectRequest();
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [emailInUseIds, setEmailInUseIds] = useState<Set<string>>(new Set());

  const extractErrorMessage = (error: any) =>
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Request failed.";

  const handleApprove = async (id: string) => {
    setProcessingId(id);
    try {
      await approveMutation.mutateAsync(id);
      toast.success("Request approved successfully.");
    } catch (error: any) {
      const message = extractErrorMessage(error);
      if (message.toLowerCase().includes("email already in use")) {
        setEmailInUseIds((prev) => new Set(prev).add(id));
      }
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (id: string) => {
    setProcessingId(id);
    try {
      await rejectMutation.mutateAsync(id);
      toast.success("Request rejected successfully.");
    } catch (error: any) {
      const message = extractErrorMessage(error);
      if (message.toLowerCase().includes("email already in use")) {
        setEmailInUseIds((prev) => new Set(prev).add(id));
      }
      toast.error(message);
    } finally {
      setProcessingId(null);
    }
  };

  const ActionButtons = ({ id }: { id: string }) => (
    <div className="flex gap-2">
      <button
        disabled={processingId === id}
        onClick={() => handleApprove(id)}
        className="flex-1 bg-[#1F2A21] hover:bg-[#243324] border border-[#2C3A2E] text-[#B8F890] text-xs font-medium py-2 rounded-full transition-colors flex items-center justify-center gap-1 disabled:opacity-60"
      >
        {processingId === id && approveMutation.isPending ? (
          <CircularProgress size={12} color="inherit" />
        ) : (
          "Approve"
        )}
      </button>
      <button
        disabled={processingId === id}
        onClick={() => handleReject(id)}
        className="flex-1 bg-[#2B1E22] hover:bg-[#352227] border border-[#3A2B31] text-[#FCA5A5] text-xs font-medium py-2 rounded-full transition-colors flex items-center justify-center gap-1 disabled:opacity-60"
      >
        {processingId === id && rejectMutation.isPending ? (
          <CircularProgress size={12} color="inherit" />
        ) : (
          "Reject"
        )}
      </button>
    </div>
  );

  const totalPages = Math.max(
    1,
    totalPagesFromApi ?? Math.ceil(totalCount / pageSize)
  );
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const filteredRequests = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return requests
      .filter((req) => req.requestContext === "UPGRADE" || !req.requestContext) // Fallback client-side filter
      .filter((req) => {
        const haystack = [
          req.companyName,
          req.companyEmail,
          req.description,
          req.status,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      });
  }, [requests, searchQuery]);

  const pageNumbers = useMemo(() => {
    const pages: number[] = [];
    const start = Math.max(1, safePage - 2);
    const end = Math.min(totalPages, safePage + 2);
    for (let i = start; i <= end; i += 1) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  return (
    <main className={"flex-1 p-4 md:p-6 mt-15 " + (isDark ? "" : "light-theme-dashboard")} style={{ background: isDark ? "" : "#F4F5F7" }}>
      <div className="bg-[#1B1926] rounded-2xl h-full flex flex-col border border-[#2A2636] shadow-[0_12px_32px_rgba(0,0,0,0.25)] credit-usage-container">
        <div className="top-0 bg-[#1B1926] p-5 border-b rounded-t-2xl border-[#2A2636] flex flex-col gap-3 md:flex-row md:items-center md:justify-between credit-usage-card">
          <div>
            <h2 className="text-xl font-semibold text-white">Contact Requests</h2>
            <p className="text-xs text-[#9CA3AF] mt-1">
              {totalCount} total request{totalCount === 1 ? "" : "s"}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative w-full md:w-[320px]">
              <input
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search by name, email, status, or note"
                className="w-full rounded-full bg-[#232133] border border-[#2F2B3D] text-sm text-white px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#B8F890]/40 focus:border-[#B8F890]/60 transition"
              />
            </div>
          </div>
        </div>

        <div className="px-5 py-4 space-y-3">
          {isLoading && (
            <p className="text-gray-400 text-sm">Loading...</p>
          )}
          {isError && (
            <p className="text-red-400 text-sm">Failed to load requests.</p>
          )}
          {!isLoading && !isError && filteredRequests.length === 0 && (
            <p className="text-gray-400 text-sm">No requests found.</p>
          )}
          <div className="overflow-hidden rounded-2xl border border-[#2A2636] bg-[#1F1D2B] credit-usage-card">
            <div className="divide-y divide-[#2A2636]">
              {filteredRequests.map((req) => (
                <div
                  key={req.id}
                  className="px-4 py-4 md:px-5 md:py-4 text-white"
                >
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-white">
                          {req.companyName || "No Name"}
                        </div>
                        <span className="bg-blue-900/40 text-blue-300 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border border-blue-500/30">
                          Upgrade
                        </span>
                      </div>
                      <div className="text-sm text-[#C7CBD4]">
                        {req.companyEmail || "No Email"}
                      </div>
                      <div className="text-xs text-[#8B90A0]">
                        {formatDateUS(req.createdAt)}
                      </div>
                      <div className="text-sm text-[#E3E6EF]">
                        {req.description || "No description"}
                      </div>
                    </div>
                    <div className="min-w-[180px]">
                      {emailInUseIds.has(req.id) ? (
                        <div className="text-xs font-semibold px-3 py-1.5 rounded-full w-fit bg-amber-900/40 text-amber-300">
                          EMAIL ALREADY IN USE
                        </div>
                      ) : req.status === "PENDING" || !req.status ? (
                        <ActionButtons id={req.id} />
                      ) : (
                        <div
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full w-fit ${req.status === "APPROVED"
                            ? "bg-green-900/40 text-green-300"
                            : "bg-red-900/40 text-red-300"
                            }`}
                        >
                          {req.status}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-2 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-[#9CA3AF]">
              Page {safePage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-full text-xs border border-[#2A2636] text-[#C7CBD4] hover:text-white hover:border-[#3A3550] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {pageNumbers.map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`px-3 py-1.5 rounded-full text-xs border ${p === safePage
                    ? "border-[#B8F890]/60 text-white bg-[#2A2F25]"
                    : "border-[#2A2636] text-[#C7CBD4] hover:text-white hover:border-[#3A3550]"
                    }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="px-3 py-1.5 rounded-full text-xs border border-[#2A2636] text-[#C7CBD4] hover:text-white hover:border-[#3A3550] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactRequestsPage;
